import React, { useState, useEffect } from 'react'
import { InputGroup, H5, Classes } from '@blueprintjs/core'
import {
  IconButton,
  Confirmation,
  Flex,
  Button,
  Toaster,
  Avatar,
  Spinner,
  GridTableStatusEnum,
  NewGridTable,
  IGridTableCell,
  IGridTableColumn,
} from '@ui'
import api, { IWorkplaceGroupEmployee, IEmployee } from '@api'
import { UseWorkplaceGroupHook } from './hooks/useWorkplaceGroup'
import { pluralize } from '@utils'
import { useReload } from '@hooks/reloadContext'
import { useSelection } from '@hooks'
import useWorkplaceGroupEmployees from './hooks/useWorkplaceGroupEmployees'
import track from '@track'
import Event from '@track/Event'
import classnames from 'classnames'
import EmployeeAndDepartmentList from './EmployeeAndDepartmentList'
import css from './employeesManageSection.module.scss'

const placeholder = (
  <span data-cy="placeholder" className={Classes.SKELETON}>
    placeholder
  </span>
)

export default function EmployeesManageSection({
  workplaceGroup,
}: {
  workplaceGroup: UseWorkplaceGroupHook
}) {
  const selection = useSelection()
  const reload = useReload()
  const [disableConfirmation, setDisableConfirmation] = useState(false)

  const [showSearch, setShowSearch] = useState(false)
  const [withUndo, setWithUndo] = useState<IWorkplaceGroupEmployee[]>([])

  const employees = useWorkplaceGroupEmployees(workplaceGroup.id)

  const removeEmployeesAPI = (employeeIds: string[]): Promise<IEmployee[]> =>
    api.workplaceGroup.removeEmployees(employeeIds, workplaceGroup.id)

  useEffect(() => {
    setWithUndo(employees.list)
  }, [employees.list])

  const helperText = selection.count
    ? `${selection.count} selected`
    : employees.query
    ? `${employees.count} filtered ${pluralize(employees.count, 'record')}`
    : `${employees.count} ${pluralize(employees.count, 'record')}`

  const columnsEmployees: IGridTableColumn<IWorkplaceGroupEmployee>[] = [
    {
      label: 'Employee',
      Cell: ({ cell, skeleton }) =>
        cell ? (
          <Flex align="center">
            <Avatar name={cell.data.name} assets={cell.data.assets} size={Avatar.Size.XS} />
            <div style={{ marginLeft: 10 }}>
              {cell.data.name}
              <div style={{ fontSize: 12, color: 'var(--grey1)' }}>{cell.data.email}</div>
            </div>
          </Flex>
        ) : (
          <div
            className={classnames({
              [Classes.SKELETON]: skeleton,
            })}
          >
            placeholder
          </div>
        ),
    },
    {
      label: 'Department / Team',
      Cell: ({ cell, skeleton }) =>
        cell ? (
          <div>
            {`${cell.data.dept ? cell.data.dept.name : ''} ${
              cell.data.team ? ' | ' + cell.data.team.name : ''
            }`}
          </div>
        ) : (
          <div
            className={classnames({
              [Classes.SKELETON]: skeleton,
            })}
          >
            placeholder
          </div>
        ),
    },
    {
      label: '',
      Cell: ({ cell, isHighlighted }) => {
        if (!cell) return null

        const employee = (cell as IGridTableCell<IWorkplaceGroupEmployee>).data

        return (
          <div
            data-button={`remove button ${employee.name}`}
            className="w-full"
            style={{ display: 'flex', justifyContent: 'flex-end' }}
          >
            {isHighlighted && (
              <IconButton
                round
                minimal
                data-button={`remove ${employee.name}`}
                tooltip={`Remove ${employee.name}`}
                className="remove-icon"
                iconName="times"
                onClick={() => {
                  const removedEmployee = employee
                  setWithUndo((prev) => [...prev.filter((i) => employee.id !== i.id)])
                  selection.clear()

                  removeEmployeesAPI([employee.id]).then(() => reload.workplaceGroups())

                  track.workplaceGroupDialog({
                    eventName: Event.RECORD_REMOVE,
                    props: {
                      component: 'Employee Manage',
                    },
                  })

                  Toaster.success({
                    action: {
                      onClick: () => {
                        api.workplaceGroup
                          .addEmployees(workplaceGroup.id, [employee.id])
                          .then(() => reload.workplaceGroups())
                        setWithUndo((prev) => [removedEmployee, ...prev])
                        track.workplaceGroupDialog({
                          eventName: Event.UNDO_CLICK,
                          props: {
                            component: 'Employee Manage',
                            actionName: 'remove',
                          },
                        })
                      },
                      text: 'Undo',
                    },
                    message: 'Employee removed from workplace group',
                  })
                }}
              />
            )}
          </div>
        )
      },
    },
  ]

  const formatItems = (items: IWorkplaceGroupEmployee[]) => {
    return items.map((i) => ({
      data: i,
    }))
  }

  const formatedItems = formatItems(withUndo)

  return (
    <>
      <Flex
        className="mt-8"
        align="center"
        justify="space-between"
        style={{
          height: 40,
        }}
      >
        <Flex align="center">
          <H5 style={{ marginBottom: 0, marginRight: 6, lineHeight: '30px' }}>Employees</H5>
          {showSearch ? (
            <InputGroup
              autoFocus
              leftIcon="search"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                employees.run(e.target.value)
              }}
              placeholder="Search employees..."
              rightElement={
                <RightElement
                  showWithoutQuery
                  query={employees.query}
                  onClear={() => {
                    if (employees.query) employees.run('')
                    setShowSearch(false)
                  }}
                  isQueryResultLoading={employees.isLoading}
                />
              }
              value={employees.query}
            />
          ) : (
            <IconButton
              round
              minimal
              data-button="search_employees"
              iconName="search"
              onClick={() => setShowSearch(true)}
            />
          )}
        </Flex>
      </Flex>
      <Flex justify="space-between">
        <Flex align="center">
          {Boolean(selection.count) && (
            <span style={{ paddingRight: 10 }}>
              <Confirmation
                shouldHide={disableConfirmation}
                enabled={!disableConfirmation}
                text={`Remove selected ${pluralize(
                  selection.count,
                  'employee'
                )} from workplace group?`}
                onConfirm={() => {
                  const employeesForDelete = withUndo.filter((i) =>
                    Object.keys(selection.items).includes(i.id)
                  )

                  const employeeIds = Object.entries(selection.items)
                    .filter(([_, value]) => Boolean(value))
                    .map(([id]) => id)

                  setWithUndo((prev) => {
                    const workplaceGroupEmployees = [
                      ...prev.filter((i) => !employeeIds.includes(i.id)),
                    ]

                    removeEmployeesAPI(employeeIds)
                      .then(() =>
                        setWithUndo((prev) => [...prev.filter((i) => !employeeIds.includes(i.id))])
                      )
                      .then(() => reload.workplaceGroups())

                    return workplaceGroupEmployees
                  })

                  track.workplaceGroupDialog({
                    eventName: Event.BATCH_ACTION,
                    props: {
                      actionName: 'Delete employees',
                      actionValue: null,
                      batchCount: employeeIds.length,
                    },
                  })

                  selection.clear()
                  Toaster.success({
                    action: {
                      onClick: () => {
                        setWithUndo((prev) => [...employeesForDelete, ...prev])
                        setDisableConfirmation(false)
                        api.workplaceGroup
                          .addEmployees(workplaceGroup.id, employeeIds)
                          .then(() => reload.workplaceGroups())

                        track.workplaceGroupDialog({
                          eventName: Event.UNDO_CLICK,
                          props: {
                            component: 'Employee Manage',
                            actionName: 'remove',
                          },
                        })
                      },
                      text: 'Undo',
                    },
                    message: `${pluralize(
                      selection.count,
                      'Employee'
                    )} removed from workplace group`,
                  })
                }}
              >
                <Button text="Remove" />
              </Confirmation>
            </span>
          )}
          <Flex align="baseline">
            {!employees.isLoading && <span style={{ color: 'var(--grey1)' }}>{helperText}</span>}
            {employees.isLoading && placeholder}
            {Boolean(selection.count) && (
              <IconButton
                minimal
                tooltip="Clear selection"
                iconName="times"
                style={{ marginLeft: 4 }}
                data-button="clear_selection"
                onClick={() => {
                  selection.clear()

                  track.workplaceGroupDialog({
                    eventName: Event.CLEAR_SELECTION,
                    props: {
                      component: 'Employee Manage',
                    },
                  })
                }}
              />
            )}
          </Flex>
        </Flex>
        <EmployeeAndDepartmentList
          onSelectEmployee={(empl) => {
            if (empl) {
              api.workplaceGroup
                .addEmployees(workplaceGroup.id, [empl.id])
                .then(() =>
                  Toaster.success({
                    message: 'Employee added to workplace group',
                  })
                )
                .then(() => reload.workplaceGroups())

              track.workplaceGroupDialog({
                eventName: Event.RECORD_ADD,
                props: {
                  component: 'Employee Manage',
                },
              })
            }
          }}
          onSelectDepartment={async (dept) => {
            if (dept) {
              api.workplaceGroup
                .addDepartmentToWorkplaceGroup(workplaceGroup.id, [dept.id])
                .then(() =>
                  Toaster.success({
                    message: 'Employees from department added to workplace group',
                  })
                )
                .then(() => reload.workplaceGroups())
                .catch((error) => {
                  Toaster.dismissAll()
                  Toaster.fail({ error })
                  reload.workplaceGroups()
                })

              track.workplaceGroupDialog({
                eventName: Event.RECORD_ADD,
                props: {
                  component: 'Employee Manage',
                },
              })
            }
          }}
        />
      </Flex>
      <div className={css.table_wrapper}>
        <NewGridTable<IWorkplaceGroupEmployee>
          status={employees.isLoading ? GridTableStatusEnum.LOADING : GridTableStatusEnum.IDLE}
          customColumnWidths={['auto', 'auto', '50px']}
          sticky={false}
          noDataMessage={employees.query ? undefined : 'No Employees associated yet'}
          columns={columnsEmployees}
          selection={selection}
          data={formatedItems}
          skeletonSize={3}
        />
      </div>
      {employees.list && employees.pageInfo.hasNextPage && (
        <Button
          minimal
          style={{ marginTop: 5 }}
          text={`View ${
            employees.count - employees.list.length > 5
              ? '5'
              : employees.count - employees.list.length
          } more`}
          onClick={() => {
            employees.loadMore()

            track.workplaceGroupDialog({
              eventName: Event.PAGE,
              props: {
                component: 'Employee Manage',
              },
            })
          }}
        />
      )}
    </>
  )
}

function RightElement({
  showWithoutQuery,
  query,
  onClear,
  isQueryResultLoading,
}: {
  query: string
  onClear: () => void
  isQueryResultLoading: boolean
  showWithoutQuery?: boolean
}) {
  if (isQueryResultLoading && query) return <Spinner size={18} className="spinner" />
  if ((showWithoutQuery !== undefined && showWithoutQuery) || query)
    return (
      <IconButton
        minimal
        iconName="times"
        onClick={() => {
          onClear?.()

          track.workplaceGroupDialog({
            eventName: Event.CLEAR_FILTER,
            props: {
              component: 'Employee Manage',
              filterName: 'Query',
              filterValue: null,
            },
          })
        }}
      />
    )
  return null
}
